import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ChatBox from '../components/ChatBox';
import ConsultationModal from '../components/stages/ConsultationModal';
import DocumentSubmissionModal from '../components/stages/DocumentSubmissionModal';
import UniversitySelectionModal from '../components/stages/UniversitySelectionModal';
import OfferLetterModal from '../components/stages/OfferLetterModal';
import DepositPaymentModal from '../components/stages/DepositPaymentModal';
import MedicalTestModal from '../components/stages/MedicalTestModal';
import FinancialDocumentsModal from '../components/stages/FinancialDocumentsModal';
import CredibilityInterviewModal from '../components/stages/CredibilityInterviewModal';
import { LogOut, Bell, User, Gift, Camera, CheckCircle, Clock, UserPlus, Phone, Mail, Calendar, MapPin, GraduationCap, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Referral status display config
const REFERRAL_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800', icon: Phone },
  enrolled: { label: 'Enrolled', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  not_interested: { label: 'Not Interested', color: 'bg-gray-100 text-gray-600', icon: null }
};

const REWARD_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  eligible: { label: 'Eligible for Reward', color: 'bg-purple-100 text-purple-800' },
  given: { label: 'Reward Given', color: 'bg-green-100 text-green-800' }
};

const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [activeTab, setActiveTab] = useState('journey');
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    city_of_residence: '',
    recent_qualification: '',
    course_interested_in: '',
    profile_picture: ''
  });
  const [referralForm, setReferralForm] = useState({
    friend_first_name: '',
    friend_last_name: '',
    friend_phone: '',
    friend_email: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [submittingReferral, setSubmittingReferral] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchDocumentRequests();
    fetchReferrals();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students/profile`);
      setProfile(response.data);
      setProfileForm({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        date_of_birth: response.data.date_of_birth || '',
        phone: response.data.phone || '',
        city_of_residence: response.data.city_of_residence || '',
        recent_qualification: response.data.recent_qualification || '',
        course_interested_in: response.data.course_interested_in || '',
        profile_picture: response.data.profile_picture || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-requests`);
      setDocumentRequests(response.data.filter(req => req.status === 'pending'));
    } catch (error) {
      console.error('Failed to fetch document requests:', error);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students/referrals`);
      setReferrals(response.data);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
    }
  };

  const handleStageClick = (stageIndex, stageName) => {
    const modalMap = {
      'Consultation': 'consultation',
      'Documents Submission': 'documents',
      'University & Course Selection': 'universities',
      'Offer Letters': 'offers',
      'Deposit Payment': 'deposit',
      'Visa Medical Test': 'medical',
      'Financial Documents': 'financial',
      'Credibility Interview': 'interview'
    };
    
    const modalToOpen = modalMap[stageName];
    if (modalToOpen) {
      setActiveModal(modalToOpen);
    }
  };

  const handleProfilePictureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileForm({ ...profileForm, profile_picture: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/students/profile`, profileForm);
      toast.success('Profile updated successfully!');
      setShowProfileDialog(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSubmitReferral = async () => {
    if (!referralForm.friend_first_name || !referralForm.friend_last_name || !referralForm.friend_phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingReferral(true);
    try {
      await axios.post(`${API_URL}/api/students/referrals`, referralForm);
      toast.success('Referral submitted successfully! Your friend will be contacted soon.');
      setShowReferralDialog(false);
      setReferralForm({
        friend_first_name: '',
        friend_last_name: '',
        friend_phone: '',
        friend_email: ''
      });
      fetchReferrals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    } finally {
      setSubmittingReferral(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const completedStages = profile?.stages.filter(s => s.status === 'completed').length || 0;
  const totalStages = profile?.stages.length || 11;
  const progressPercentage = (completedStages / totalStages) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-lime-50">
      <nav className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_edujourney-29/artifacts/h5igihub_britsfly.png" 
              alt="BritsFly Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading font-bold text-2xl text-foreground">BritsFly</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="profile-btn"
              onClick={() => setShowProfileDialog(true)}
              variant="outline"
              className="rounded-full"
            >
              <User className="h-4 w-4 mr-2" />
              My Profile
            </Button>
            <div className="text-right">
              <p className="font-heading font-semibold text-foreground">{user?.full_name}</p>
              <p className="text-sm text-muted-foreground">Student</p>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
            Welcome back, {profile?.first_name || user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground text-lg">Track your study abroad journey</p>
        </div>

        {documentRequests.length > 0 && (
          <Card className="mb-6 border-warning bg-warning/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-warning">
                <Bell className="h-5 w-5" />
                Document Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {documentRequests.map((req) => (
                  <div key={req.id} className="p-3 bg-white rounded-lg">
                    <p className="font-medium text-foreground">{req.document_name}</p>
                    <p className="text-sm text-muted-foreground">{req.notes}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading font-semibold text-2xl">Your Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-body text-muted-foreground">
                  {completedStages} of {totalStages} stages completed
                </span>
                <span className="font-heading font-bold text-2xl text-primary">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <Progress value={progressPercentage} className="h-3" />
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Journey and Referrals */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="journey" data-testid="journey-tab">
              <GraduationCap className="h-4 w-4 mr-2" />
              Application Journey
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="referrals-tab">
              <Gift className="h-4 w-4 mr-2" />
              Refer a Friend
            </TabsTrigger>
          </TabsList>

          <TabsContent value="journey" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading font-semibold text-2xl">Application Journey</CardTitle>
                    <p className="text-muted-foreground text-sm">Click on any stage to take action</p>
                  </CardHeader>
                  <CardContent>
                    {profile && (
                      <div className="space-y-4">
                        {profile.stages.map((stage, index) => (
                          <div
                            key={index}
                            data-testid={`stage-${index}`}
                            onClick={() => handleStageClick(index, stage.name)}
                            className={`stage-node p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                              stage.status === 'completed' ? 'border-success bg-success/5' :
                              index === profile.current_stage ? 'border-warning bg-warning/5' :
                              'border-muted bg-muted'
                            } hover:shadow-lg hover:scale-[1.01]`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                                  stage.status === 'completed' ? 'bg-success text-white' :
                                  index === profile.current_stage ? 'bg-warning text-white' :
                                  'bg-muted-foreground/20 text-muted-foreground'
                                }`}>
                                  {stage.status === 'completed' ? '✓' : index + 1}
                                </div>
                                <div>
                                  <h3 className="font-heading font-semibold text-lg">{stage.name}</h3>
                                  <p className="text-sm text-muted-foreground capitalize">{stage.status}</p>
                                </div>
                              </div>
                              {stage.status !== 'completed' && index <= profile.current_stage && (
                                <Button variant="ghost" size="sm" className="rounded-full">
                                  Take Action
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-5">
                {profile && (
                  <div className="sticky top-6">
                    <ChatBox studentId={profile.id} currentUserRole="student" />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="referrals" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading font-semibold text-2xl flex items-center gap-2">
                          <Gift className="h-6 w-6 text-primary" />
                          Refer a Friend
                        </CardTitle>
                        <p className="text-muted-foreground text-sm mt-1">
                          Share BritsFly with your friends and earn rewards!
                        </p>
                      </div>
                      <Button
                        data-testid="add-referral-btn"
                        onClick={() => setShowReferralDialog(true)}
                        className="rounded-full bg-primary hover:bg-primary/90"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Referral
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {referrals.length === 0 ? (
                      <div className="text-center py-12">
                        <Gift className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No referrals yet</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Refer your friends and earn exciting rewards when they enroll!
                        </p>
                        <Button
                          onClick={() => setShowReferralDialog(true)}
                          className="mt-4 rounded-full"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Refer Your First Friend
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {referrals.map((referral) => {
                          const statusConfig = REFERRAL_STATUS_CONFIG[referral.status];
                          const rewardConfig = REWARD_STATUS_CONFIG[referral.reward_status];
                          
                          return (
                            <div
                              key={referral.id}
                              data-testid={`referral-card-${referral.id}`}
                              className="p-5 rounded-xl border-2 border-border hover:border-primary/30 transition-all"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h3 className="font-heading font-semibold text-lg text-foreground">
                                    {referral.friend_first_name} {referral.friend_last_name}
                                  </h3>
                                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                                    <Phone className="h-3 w-3" />
                                    {referral.friend_phone}
                                  </p>
                                  {referral.friend_email && (
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                      <Mail className="h-3 w-3" />
                                      {referral.friend_email}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusConfig.color}`}>
                                    {statusConfig.label}
                                  </span>
                                  <span className={`text-xs px-3 py-1 rounded-full ${rewardConfig.color}`}>
                                    {rewardConfig.label}
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Referred on: {new Date(referral.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-5">
                <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
                  <CardHeader>
                    <CardTitle className="font-heading font-semibold text-xl">How It Works</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">1</div>
                        <div>
                          <p className="font-medium">Share your friend's details</p>
                          <p className="text-sm text-muted-foreground">Add their name and contact info</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">2</div>
                        <div>
                          <p className="font-medium">We reach out</p>
                          <p className="text-sm text-muted-foreground">Our team contacts your friend</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">3</div>
                        <div>
                          <p className="font-medium">Your friend enrolls</p>
                          <p className="text-sm text-muted-foreground">They start their journey with BritsFly</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-success text-white flex items-center justify-center text-sm font-bold">
                          <Gift className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">You earn rewards!</p>
                          <p className="text-sm text-muted-foreground">Get exciting rewards for successful referrals</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="font-heading font-semibold text-xl">Your Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-3xl font-bold text-primary">{referrals.length}</p>
                        <p className="text-sm text-muted-foreground">Total Referrals</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-3xl font-bold text-success">
                          {referrals.filter(r => r.status === 'enrolled').length}
                        </p>
                        <p className="text-sm text-muted-foreground">Enrolled</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-3xl font-bold text-purple-600">
                          {referrals.filter(r => r.reward_status === 'eligible' || r.reward_status === 'given').length}
                        </p>
                        <p className="text-sm text-muted-foreground">Rewards Earned</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-xl">
                        <p className="text-3xl font-bold text-warning">
                          {referrals.filter(r => r.status === 'pending' || r.status === 'contacted').length}
                        </p>
                        <p className="text-sm text-muted-foreground">In Progress</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">My Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center overflow-hidden border-4 border-primary/20">
                  {profileForm.profile_picture ? (
                    <img src={profileForm.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-16 w-16 text-muted-foreground" />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                  <Camera className="h-5 w-5 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    data-testid="profile-picture-input"
                  />
                </label>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Click the camera icon to upload a photo</p>
            </div>

            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  First Name
                </Label>
                <Input
                  id="first_name"
                  data-testid="profile-first-name"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  placeholder="John"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Last Name
                </Label>
                <Input
                  id="last_name"
                  data-testid="profile-last-name"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  placeholder="Doe"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date of Birth
                </Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  data-testid="profile-dob"
                  value={profileForm.date_of_birth}
                  onChange={(e) => setProfileForm({ ...profileForm, date_of_birth: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  data-testid="profile-phone"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  value={profile?.email || ''}
                  disabled
                  className="rounded-xl bg-muted"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  City of Residence
                </Label>
                <Input
                  id="city"
                  data-testid="profile-city"
                  value={profileForm.city_of_residence}
                  onChange={(e) => setProfileForm({ ...profileForm, city_of_residence: e.target.value })}
                  placeholder="Mumbai"
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Academic Information */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Academic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qualification" className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    Recent Qualification
                  </Label>
                  <Input
                    id="qualification"
                    data-testid="profile-qualification"
                    value={profileForm.recent_qualification}
                    onChange={(e) => setProfileForm({ ...profileForm, recent_qualification: e.target.value })}
                    placeholder="e.g., BTech CSE"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="course" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Course Interested In
                  </Label>
                  <Input
                    id="course"
                    data-testid="profile-course"
                    value={profileForm.course_interested_in}
                    onChange={(e) => setProfileForm({ ...profileForm, course_interested_in: e.target.value })}
                    placeholder="e.g., MSc Data Science"
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>

            <Button
              data-testid="save-profile-btn"
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl flex items-center gap-2">
              <Gift className="h-6 w-6 text-primary" />
              Refer a Friend
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Share the gift of education! Enter your friend's details and we'll reach out to help them start their study abroad journey.
            </p>

            <div className="space-y-2">
              <Label htmlFor="friend_first_name">Friend's First Name *</Label>
              <Input
                id="friend_first_name"
                data-testid="referral-first-name"
                value={referralForm.friend_first_name}
                onChange={(e) => setReferralForm({ ...referralForm, friend_first_name: e.target.value })}
                placeholder="John"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="friend_last_name">Friend's Last Name *</Label>
              <Input
                id="friend_last_name"
                data-testid="referral-last-name"
                value={referralForm.friend_last_name}
                onChange={(e) => setReferralForm({ ...referralForm, friend_last_name: e.target.value })}
                placeholder="Smith"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="friend_phone">Friend's Phone Number *</Label>
              <Input
                id="friend_phone"
                data-testid="referral-phone"
                value={referralForm.friend_phone}
                onChange={(e) => setReferralForm({ ...referralForm, friend_phone: e.target.value })}
                placeholder="+91 98765 43210"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="friend_email">Friend's Email Address (Optional)</Label>
              <Input
                id="friend_email"
                type="email"
                data-testid="referral-email"
                value={referralForm.friend_email}
                onChange={(e) => setReferralForm({ ...referralForm, friend_email: e.target.value })}
                placeholder="friend@email.com"
                className="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">If provided, we'll send them a friendly introduction email</p>
            </div>

            <Button
              data-testid="submit-referral-btn"
              onClick={handleSubmitReferral}
              disabled={submittingReferral}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {submittingReferral ? 'Submitting...' : 'Submit Referral'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Modals */}
      <ConsultationModal
        isOpen={activeModal === 'consultation'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <DocumentSubmissionModal
        isOpen={activeModal === 'documents'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <UniversitySelectionModal
        isOpen={activeModal === 'universities'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <OfferLetterModal
        isOpen={activeModal === 'offers'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
      />
      <DepositPaymentModal
        isOpen={activeModal === 'deposit'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <MedicalTestModal
        isOpen={activeModal === 'medical'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <FinancialDocumentsModal
        isOpen={activeModal === 'financial'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
      <CredibilityInterviewModal
        isOpen={activeModal === 'interview'}
        onClose={() => setActiveModal(null)}
        studentId={profile?.id}
        onSuccess={fetchProfile}
      />
    </div>
  );
};

export default StudentDashboard;
